/**
 * O5 — Narrative-beat detection: surface high-drama moments to the
 * spectator overlay as transient hero cards.
 *
 * Pure transform — runs over the full event stream and emits a list
 * of `Accolade` rows the renderer pegs onto specific ticks. Scrubbing
 * back / forward through a replay re-derives identical accolades
 * (no state). Deterministic for the same input.
 *
 * Calibration: keep the bar high. Accolades that fire every turn
 * lose meaning. The v1 set is intentionally sparse — FIRST_BLOOD,
 * BREACH (per side), AMMO_COOKOFF, KILL — five total moments at
 * most across a match. Future additions go through the same gate:
 * "would this read as a Twitch-stream hero card, or just noise?"
 */

import type { RenderEvent } from "../replay/events";

export type AccoladeKind =
  | "first_blood"
  | "breach"
  | "ammo_cookoff"
  | "decisive_crit"
  | "kill";

export type AccoladeSeverity = "alert" | "warn" | "info";

export interface Accolade {
  /** Unique stable id (kind + side + tick). */
  id: string;
  /** Tick at which the triggering event fired. */
  tick: number;
  /** The side whose action / state earned the accolade. */
  side: "A" | "B" | "—";
  kind: AccoladeKind;
  /** Hero text (short, ALL-CAPS-friendly). */
  label: string;
  /** Smaller context line under the hero text. */
  subtext: string;
  /** Severity → token-driven tint for the flash. */
  severity: AccoladeSeverity;
}

/**
 * Detect every accolade in `stream` across the full match. Result is
 * ordered by tick ascending and deduplicated by `id`.
 *
 * Detection rules:
 *
 *   - FIRST_BLOOD: the first `hit` event with any damage > 0 in the
 *     match. Side = shooter — but the stream's `hit` event only
 *     carries `victim`, so we credit the OTHER side. Two-mech battles
 *     only (consistent with the rest of the viewer's assumptions).
 *
 *   - BREACH: the first `exposed` event for each side. Two accolades
 *     max (one per side), each at the tick the side's first zone
 *     reached internal-only state.
 *
 *   - AMMO_COOKOFF: every `ammo_explosion` event (rare; high drama).
 *
 *   - DECISIVE_CRIT: every `crit_engine` / `crit_gyro` event. Skips
 *     `crit_actuator` / `crit_tool_destroyed` to keep the bar high.
 *
 *   - KILL: every `kill` event. The match-ending kill gets a slightly
 *     louder subtext ("MATCH POINT"); the others get a neutral one.
 */
export function detectAccolades(stream: ReadonlyArray<RenderEvent>): Accolade[] {
  const out: Accolade[] = [];
  let firstBloodFired = false;
  const breachFiredFor = new Set<"A" | "B">();
  const killTicks: number[] = [];
  let lastKillTick = -1;
  for (const e of stream) {
    if (e.kind === "kill") {
      killTicks.push(e.tick);
      if (e.tick > lastKillTick) lastKillTick = e.tick;
    }
  }
  for (const e of stream) {
    switch (e.kind) {
      case "hit": {
        if (!firstBloodFired && e.damage > 0) {
          firstBloodFired = true;
          const credit = e.victim === "A" ? "B" : "A";
          out.push({
            id: `first_blood-${e.tick}`,
            tick: e.tick,
            side: credit,
            kind: "first_blood",
            label: "FIRST BLOOD",
            subtext: `${zoneShort(e.zone)} struck`,
            severity: "warn",
          });
        }
        break;
      }
      case "exposed": {
        if (!breachFiredFor.has(e.victim)) {
          breachFiredFor.add(e.victim);
          out.push({
            id: `breach-${e.victim}-${e.tick}`,
            tick: e.tick,
            side: e.victim,
            kind: "breach",
            label: "ARMOR BREACH",
            subtext: `${e.victim} ${zoneShort(e.zone)} exposed`,
            severity: "alert",
          });
        }
        break;
      }
      case "ammo_explosion": {
        out.push({
          id: `ammo_cookoff-${e.victim}-${e.tick}`,
          tick: e.tick,
          side: e.victim,
          kind: "ammo_cookoff",
          label: "AMMO COOKOFF",
          subtext: `${e.victim} reactor breach`,
          severity: "alert",
        });
        break;
      }
      case "crit_engine":
      case "crit_gyro": {
        out.push({
          id: `decisive_crit-${e.kind}-${e.victim}-${e.tick}`,
          tick: e.tick,
          side: e.victim,
          kind: "decisive_crit",
          label: e.kind === "crit_engine" ? "ENGINE CRIT" : "GYRO CRIT",
          subtext: `${e.victim} reactor systems failing`,
          severity: "warn",
        });
        break;
      }
      case "kill": {
        const isMatchPoint = e.tick === lastKillTick;
        out.push({
          id: `kill-${e.victim}-${e.tick}`,
          tick: e.tick,
          side: e.victim === "A" ? "B" : "A",
          kind: "kill",
          label: isMatchPoint ? "MATCH POINT" : "DOWN",
          subtext: `${e.victim} destroyed — ${e.cause}`,
          severity: "alert",
        });
        break;
      }
    }
  }
  return out;
}

/**
 * Find the accolade that should be visible at `tick`. Returns the most
 * recent accolade triggered within `windowTicks` of `tick`. When
 * multiple fire on the same tick (e.g. breach + kill), the highest-
 * severity one wins; ties resolve to the latest-listed.
 *
 * Returns `null` outside the window so the caller can fade the flash
 * out cleanly.
 */
export function visibleAccoladeAt(
  accolades: ReadonlyArray<Accolade>,
  tick: number,
  windowTicks = 2,
): Accolade | null {
  let best: Accolade | null = null;
  let bestRank = -1;
  for (const a of accolades) {
    if (a.tick > tick) break;
    if (tick - a.tick > windowTicks) continue;
    const rank = severityRank(a.severity);
    if (rank >= bestRank) {
      best = a;
      bestRank = rank;
    }
  }
  return best;
}

function severityRank(s: AccoladeSeverity): number {
  switch (s) {
    case "alert":
      return 2;
    case "warn":
      return 1;
    case "info":
      return 0;
  }
}

function zoneShort(zone: string): string {
  switch (zone) {
    case "head":
      return "HEAD";
    case "front_ct":
      return "FRONT-CT";
    case "rear_ct":
      return "REAR-CT";
    case "l_side":
      return "L-SIDE";
    case "r_side":
      return "R-SIDE";
    case "l_leg":
      return "L-LEG";
    case "r_leg":
      return "R-LEG";
    default:
      return zone.toUpperCase();
  }
}
